import React, { useContext, useCallback, useEffect, useState } from 'react'
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute'
import { styled } from 'linaria/react'
import { theme } from '../Theme'
import { StructuredProductData } from '@jetshop/core/components/StructuredData'
import HelloRetailCarousel from '../HelloRetail/Carousel'
import { useSiteSettingsQuery } from '../../hooks/useSiteSettings'
import {
   HELLO_RETAIL_SETTINGS,
   Product_Page_Settings,
} from '../CustomElements/elementTypes'
import {
   getHelloRetailProducts,
   HelloRetailHelper,
} from '../HelloRetail/HelloRetailHelper'
import { Breadcrumbs } from '../Core/BreadCrumbs/BreadCrumbs'
import ProductPageSidebar from './UI/ProductPageSidebar'
import { Above, Below } from '@jetshop/ui/Breakpoints'
import ProductDetails from './UI/ProductDetails'
import UseFormatProductPage from '../Core/Hooks/UseFormatProductPage'
import ProductImageGallery from './UI/ProductImageGallery'
import ProductInfo from './UI/ProductInfo'
import { UseHelloRetail } from '../HelloRetail/UseHelloRetail'
import ChannelContext from '@jetshop/core/components/ChannelContext/ChannelContext'
import ProductRelatedProducts from './UI/ProductRelatedProducts'
import { Helmet } from 'react-helmet-async'
import ProductAddToCartFixed from './UI/ProductAddToCartFixed'
import useUpdateProductInformation from './Hooks/useUpdateProductInformation'
import { useHandleProductNumberAndPackage } from './Hooks/useHandleProductNumberAndPackage'
import { useHandleActiveVariant } from './Hooks/useHandleActiveVariant'
import { useHandleBrandCat } from './Hooks/useHandleBrandCat'
import useTestFreakReviews from './Hooks/useTestFreakReviews'
import { useProductViewEvent } from './Hooks/useProductViewEvent'
import { HelloRetailCarouselFetcher } from '../HelloRetail/HelloRetailCarouselFetcher'

const ProductPageWrapper = styled('div')`
   position: relative;
   background-color: ${theme.colors.white};
   & {
      ${theme.below.md} {
         padding-top: 1.75rem !important;
      }
   }

   .breadcrumb-wrapper {
      /* background-color: ${theme.colors.white}; */
   }
`

const ProductPageContainer = styled('div')`
   & {
      ${theme.below.md} {
         margin-top: 0 !important;
      }
   }

   & {
      ${theme.below.md} {
         padding-right: 0px !important;
         padding-left: 0px !important;
      }
   }

   hr {
      ${theme.below.md} {
         margin-top: 1.5rem !important;
         margin-bottom: 1.5rem !important;
      }
   }

   .product-information {
      background: ${theme.colors.white};
      border-radius: 0 32px 0 0;
      box-shadow: 0 3px 16px rgba(0, 0, 0, 0.1);
      position: relative;
      /* z-index: 10; */
   }

   .productpage-details-desktop {
      background: ${theme.colors.white};
      border-radius: 0 32px 32px 0;
      box-shadow: 0 3px 16px rgba(0, 0, 0, 0.1);
      margin-left: -2rem;
      top: 12rem;
      z-index: 0;
   }

   @media (max-width: 991px) {
      .product-information {
         border-radius: 1rem;
      }
   }
`

const HelloRetailWrapper = styled.div`
   /* > div:nth-child(odd) {
      background-color: ${theme.colors.white};
   }
   > div:nth-child(even) {
      background-color: #d4d4d4;
   } */
`

const Product = ({ result: { data, loading }, product }) => {
   const selectedChannel = useContext(ChannelContext)?.selectedChannel
   const [getSetting, loadingSetting] = useSiteSettingsQuery()
   const helloRetailSetting = getSetting(null, HELLO_RETAIL_SETTINGS)
   const productSettings = getSetting(null, Product_Page_Settings)
   const productData = UseFormatProductPage(product)
   useUpdateProductInformation(productData?.articleNumber)
   useHandleActiveVariant(productData?.selectedVariant)
   useHandleProductNumberAndPackage(productData)
   useProductViewEvent(product?.articleNumber)
   const { structuredReviewData } = useTestFreakReviews()
   const { brandCat, brandCatIsLoading } = useHandleBrandCat(product)

   const OTHER_LOOKED_AT = HelloRetailHelper(
      'OTHER_LOOKED_AT',
      helloRetailSetting
   )
   const OTHER_BOUGHT = HelloRetailHelper('OTHER_BOUGHT', helloRetailSetting)
   const LATEST_VISITED = HelloRetailHelper(
      'LATEST_VISITED',
      helloRetailSetting
   )

   const { helloRetailRecoms, helloRetailLoading } = UseHelloRetail([
      OTHER_LOOKED_AT,
      OTHER_BOUGHT,
      LATEST_VISITED,
   ])

   // Return early if there is no product
   if (!product) return <></>

   if (loading) {
      return <h3>Laddar</h3>
   }

   const parents = getParentOrCanonicalRoute(data?.route?.parents, product)

   return (
      <ProductPageWrapper>
         {structuredReviewData && (
            <Helmet key={structuredReviewData}>
               <script type="application/ld+json">
                  {structuredReviewData}
               </script>
            </Helmet>
         )}
         <StructuredProductData
            initialVariant={productData?.selectedVariant}
            product={product}
         />
         <div className="breadcrumb-wrapper px-128 py-32">
            <Breadcrumbs
               parents={parents}
               breadcrumbText={product.breadcrumbText}
            />
         </div>

         <ProductPageContainer className="container-fluid">            
            <Above breakpoint="lg">
               <div className="row">
                  <div className="col-12 col-lg-7 pl-128 pr-64 py-64 product-information">
                     <div className="product-information-wrapper">
                        <ProductImageGallery
                           loading={loading}
                           productSettings={productSettings}
                           product={productData}
                           selectedChannelId={selectedChannel?.id}
                           className="mb-64"
                        ></ProductImageGallery>
                        <ProductInfo
                           productData={productData}
                           brand={brandCat}
                        ></ProductInfo>
                     </div>
                  </div>
                  <ProductPageSidebar
                     productData={productData}
                     brandCat={brandCat}
                     productSettings={productSettings}
                  ></ProductPageSidebar>
               </div>
            </Above>

            <Below breakpoint="lg">
               <div className="row m-0 p-0">
                  <div className="col-12 col-lg-7 pl-128 pr-64 py-64 product-information">
                     <div className="product-information-wrapper">
                        <ProductImageGallery
                           loading={loading}
                           productSettings={productSettings}
                           product={productData}
                           selectedChannelId={selectedChannel?.id}
                           className="mb-64"
                        ></ProductImageGallery>
                        <ProductDetails
                           productData={productData}
                           brandCat={brandCat}
                           productSettings={productSettings}
                        ></ProductDetails>
                        <ProductInfo
                           productData={productData}
                           brand={brandCat}
                        ></ProductInfo>
                        <ProductRelatedProducts />
                     </div>
                  </div>
                  <ProductAddToCartFixed />
               </div>
            </Below>
         </ProductPageContainer>


         {helloRetailRecoms && (
            <HelloRetailWrapper className="py-64">
               <HelloRetailCarouselFetcher
                  className="py-32"
                  products={getHelloRetailProducts(
                     helloRetailRecoms,
                     OTHER_LOOKED_AT
                  )}
                  loading={helloRetailLoading}
                  title={OTHER_LOOKED_AT?.title}
                  render={modifiedResults => (
                     <HelloRetailCarousel {...modifiedResults} />
                  )}
               />

               <HelloRetailCarouselFetcher
                  className="py-32"
                  products={getHelloRetailProducts(
                     helloRetailRecoms,
                     OTHER_BOUGHT
                  )}
                  loading={helloRetailLoading}
                  title={OTHER_BOUGHT?.title}
                  render={modifiedResults => (
                     <HelloRetailCarousel {...modifiedResults} />
                  )}
               />

               <HelloRetailCarouselFetcher
                  className="py-32"
                  products={getHelloRetailProducts(
                     helloRetailRecoms,
                     LATEST_VISITED
                  )}
                  loading={helloRetailLoading}
                  title={LATEST_VISITED?.title}
                  render={modifiedResults => (
                     <HelloRetailCarousel {...modifiedResults} />
                  )}
               />

               {/* <HelloRetailCarousel
                  className="py-32"
                  products={getHelloRetailProducts(
                     helloRetailRecoms,
                     OTHER_LOOKED_AT
                  )}
                  loading={helloRetailLoading}
                  title={OTHER_LOOKED_AT?.title}
               ></HelloRetailCarousel>
               <HelloRetailCarousel
                  className="py-32"
                  products={getHelloRetailProducts(
                     helloRetailRecoms,
                     OTHER_BOUGHT
                  )}
                  loading={helloRetailLoading}
                  title={OTHER_BOUGHT?.title}
               ></HelloRetailCarousel>
               <HelloRetailCarousel
                  className="py-32"
                  products={getHelloRetailProducts(
                     helloRetailRecoms,
                     LATEST_VISITED
                  )}
                  loading={helloRetailLoading}
                  title={LATEST_VISITED?.title}
               ></HelloRetailCarousel> */}
            </HelloRetailWrapper>
         )}
      </ProductPageWrapper>
   )
}

export default Product